import { useMemo, useState } from "react";
import { ACTION_GROUPS, API_SECTIONS } from "../apiref/data";

// Read-only API Reference panel (spec §6.2, Appendix A). The method tables come
// from Appendix A; the action-group table is parsed live from the Hiwonder.py
// stub, so nothing here can drift from what the simulator actually accepts.

export default function ApiReference({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();

  const sections = useMemo(() => {
    if (!query) return API_SECTIONS;
    return API_SECTIONS.map((s) => ({
      ...s,
      rows: s.rows.filter((r) =>
        `${r.cls} ${r.member} ${r.params} ${r.desc}`.toLowerCase().includes(query),
      ),
    })).filter((s) => s.rows.length > 0);
  }, [query]);

  const groups = useMemo(() => {
    if (!query) return ACTION_GROUPS;
    return ACTION_GROUPS.filter(
      (g) => g.name.toLowerCase().includes(query) || String(g.id).includes(query),
    );
  }, [query]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="API Reference"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-slate-700 bg-slate-800 px-4 py-3">
          <h2 className="text-lg font-semibold text-slate-100">API Reference</h2>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter methods and action groups…"
            className="ml-2 flex-1 rounded bg-slate-900 px-3 py-1.5 text-sm text-slate-100 outline-none ring-1 ring-slate-700 focus:ring-sky-500"
            autoFocus
          />
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-slate-700 px-3 py-1.5 text-sm font-medium text-slate-100 hover:bg-slate-600"
          >
            Close
          </button>
        </div>

        <div className="overflow-auto p-4 text-sm">
          {sections.map((s) => (
            <section key={s.id} className="mb-6">
              <h3 className="mb-1 font-mono text-base font-semibold text-sky-300">
                import {s.module}
              </h3>
              {s.note && <p className="mb-2 text-xs text-slate-400">{s.note}</p>}
              <div className="overflow-x-auto rounded border border-slate-800">
                <table className="w-full border-collapse text-left">
                  <thead className="bg-slate-800 text-xs uppercase text-slate-400">
                    <tr>
                      <th className="px-2 py-1 font-medium">Class</th>
                      <th className="px-2 py-1 font-medium">Method / member</th>
                      <th className="px-2 py-1 font-medium">Parameters</th>
                      <th className="px-2 py-1 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.rows.map((r, i) => (
                      <tr key={i} className="border-t border-slate-800 align-top">
                        <td className="px-2 py-1 text-slate-400">{r.cls}</td>
                        <td className="px-2 py-1 font-mono text-emerald-300">{r.member}</td>
                        <td className="px-2 py-1 text-slate-300">{r.params}</td>
                        <td className="px-2 py-1 text-slate-300">{r.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          <section className="mb-2">
            <h3 className="mb-1 text-base font-semibold text-sky-300">
              Action groups{" "}
              <span className="text-xs font-normal text-slate-400">({groups.length})</span>
            </h3>
            <p className="mb-2 text-xs text-slate-400">
              IDs for <span className="font-mono text-emerald-300">runActionGroup(id, repeat)</span>
              . Duration is one cycle. Parsed from the live stub catalog.
            </p>
            <div className="overflow-x-auto rounded border border-slate-800">
              <table className="w-full border-collapse text-left">
                <thead className="bg-slate-800 text-xs uppercase text-slate-400">
                  <tr>
                    <th className="px-2 py-1 font-medium">ID</th>
                    <th className="px-2 py-1 font-medium">Name</th>
                    <th className="px-2 py-1 font-medium">Cycle (ms)</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.id} className="border-t border-slate-800">
                      <td className="px-2 py-1 font-mono text-slate-200">{g.id}</td>
                      <td className="px-2 py-1 font-mono text-emerald-300">{g.name}</td>
                      <td className="px-2 py-1 text-slate-300">{g.durationMs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {sections.length === 0 && groups.length === 0 && (
            <p className="text-slate-400">No matches for “{q}”.</p>
          )}
        </div>
      </div>
    </div>
  );
}
